import React, { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledButtonDropdown,
  UncontrolledDropdown,
} from "reactstrap";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import BreadCrumb from "Components/Common/BreadCrumb";
import DeleteModal from "Components/Common/DeleteModal";
import Permission from "Components/Common/Permission";
import Permissions from "Components/Common/Permissions";
import TableContainer from "Components/Common/TableContainer";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { useProfile } from "Components/Hooks/UserHooks";
import FileIcon from "Components/Common/FileIcon";
import { normalize } from "common/lib/normalize";
import { getSortObj } from "common/lib/tableDataOptions";
import { isAllowed } from "common/lib/permissionUtils";
import {
  deleteAssessment,
  getAssessments,
  getAssessmentTypes,
} from "common/services/assessments.service";
import CapacityAssessmentRating from "../CapacityAssessments/ViewCapacityAssessment/CapacityAssessmentRating";
import RiskAssessmentRating from "../RiskAssessments/RiskAssessmentRating";
import AddAssessment from "./AddAssessment";
import Import from "./Import";
import UpdateAssessment from "./UpdateAssessment";

type DateRange = {
  fromDate: Date | null;
  toDate: Date | null;
};

type FilterValue = {
  assessmentType: any;
  dateRange: DateRange;
};

const AllAssessments = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userProfile } = useProfile();
  const [searchParams, setSearchParams] = useSearchParams();

  usePageActivity(location.pathname, "All Assessments");

  const [params] = useState({
    page: Number(searchParams.get("page")) || 1,
    pageSize: Number(searchParams.get("pageSize")) || 20,
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1,
    pageSize: params.pageSize,
  });

  const setPage = (newPage: number) => {
    setPagination((prev) => ({
      ...prev,
      pageIndex: newPage - 1,
    }));
  };

  const [sorted, setSorted] = useState(() => {
    const sortParam = searchParams.get("sort");
    return sortParam ? JSON.parse(sortParam) : [{ id: "createdAt", desc: true }];
  });

  const defaultFilterValue: FilterValue = {
    assessmentType: null,
    dateRange: {
      fromDate: null,
      toDate: null,
    },
  };

  const [data, setData] = useState<any[]>([]);
  const [meta, setMeta] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState(searchParams.get("search") || "");
  const [filterValue, setFilterValue] = useState<FilterValue>(() => {
    const filterParam = searchParams.get("filter");
    return filterParam ? JSON.parse(filterParam) : defaultFilterValue;
  });
  const [selectedRecord, setSelectedRecord] = useState<any>({});
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteMultipleModal, setDeleteMultipleModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [selectedCheckBox, setSelectedCheckBox] = useState<any[]>([]);
  const [assessmentTypeOptions, setAssessmentTypeOptions] = useState<any[]>([]);

  useEffect(() => {
    const fetchAssessmentTypes = async () => {
      try {
        const response: any = await getAssessmentTypes({
          pagination: {
            page: 1,
            pageSize: 100,
          },
          sort: ["name"],
        });

        setAssessmentTypeOptions(
          normalize(response.data).map((item: any) => ({
            value: item.id,
            label: item.name,
          }))
        );
      } catch (error) {
      }
    };

    fetchAssessmentTypes();
  }, []);

  const unCheckAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    if (checkall) checkall.checked = false;
    setSelectedCheckBox([]);
  }, []);

  const fetchData = () => {
    setPage(1);
    setSorted([{ id: "createdAt", desc: true }]);
    setSearchValue("");
    setFilterValue(defaultFilterValue);
  };

  const onSelectCheckbox = () => {
    const ele = document.querySelectorAll(".tableCheckbox:checked");
    setSelectedCheckBox(Array.from(ele));
  };

  const checkedAll = useCallback(() => {
    const checkall: any = document.getElementById("checkBoxAll");
    const ele = document.querySelectorAll(".tableCheckbox");

    if (checkall?.checked) {
      ele.forEach((item: any) => {
        item.checked = true;
      });
    } else {
      ele.forEach((item: any) => {
        item.checked = false;
      });
    }

    onSelectCheckbox();
  }, []);

  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: FilterValue;
    }) => {
      const params = new URLSearchParams(searchParams);
      params.set("page", String(page));
      params.set("sort", JSON.stringify(sorted));
      params.set("search", searchValue);
      params.set("filter", JSON.stringify(filterValue));
      setSearchParams(params, { replace: true });

      const sortObj = getSortObj(sorted);
      const andConditions: any[] = [];

      if (filterValue?.assessmentType?.value) {
        andConditions.push({
          assessmentType: {
            id: filterValue.assessmentType.value,
          },
        });
      }

      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate = new Date(filterValue.dateRange.fromDate).toLocaleDateString("en-CA");
        const toDate = new Date(filterValue.dateRange.toDate).toLocaleDateString("en-CA");
        andConditions.push({
          reportDate: {
            $between: [fromDate, toDate],
          },
        });
      }

      andConditions.push({
        $or: [
          {
            title: {
              $containsi: searchValue,
            },
          },
          {
            serialNumber: {
              $containsi: searchValue,
            },
          },
          {
            contractor: {
              contractorName: {
                $containsi: searchValue,
              },
            },
          },
          {
            agency: {
              name: {
                $containsi: searchValue,
              },
            },
          },
        ],
      });

      try {
        setLoading(true);
        unCheckAll();
        const response: any = await getAssessments({
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: [
            "assessmentType",
            "contractor",
            "agency",
            "overallRating",
            "overallRiskRating",
            "reportFile",
          ],
          filters: {
            $and: andConditions,
          },
        });

        setData(normalize(response.data));
        setMeta(response.meta);
      } catch (error) {
        toast.error("Error while fetching assessments", {
          autoClose: 1000,
        });
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize, searchParams, setSearchParams, unCheckAll]
  );

  const fetchUpdatedData = useCallback(() => {
    onPageChange({
      page: pagination.pageIndex + 1,
      sorted,
      searchValue,
      filterValue,
    });
  }, [filterValue, onPageChange, pagination.pageIndex, searchValue, sorted]);

  const onClickDelete = useCallback((record: any) => {
    setSelectedRecord(record);
    setDeleteModal(true);
  }, []);

  const handleDelete = async () => {
    if (!selectedRecord) return;

    try {
      setDeleting(true);
      await deleteAssessment(selectedRecord);
      fetchUpdatedData();
      setDeleteModal(false);
      toast.success("Assessment deleted successfully");
    } catch (error) {
      toast.error("Error while deleting assessment");
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteMultiple = async () => {
    const selectedIds = selectedCheckBox.map((item: any) => item.value);
    if (!selectedIds.length) return;

    try {
      setDeleting(true);
      await Promise.all(
        selectedIds.map((id: any) => deleteAssessment({ id }))
      );
      toast.success("Assessments deleted successfully.");
      fetchUpdatedData();
      setDeleteMultipleModal(false);
    } catch (error) {
      toast.error("Error while deleting assessments.");
    } finally {
      setDeleting(false);
    }
  };

  const [columnVisibility] = useState({
    checkBoxColumn: isAllowed(userProfile?.permissions, [
      {
        resource: "assessment",
        action: "delete",
      },
    ]),
  });

  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onClick={() => checkedAll()}
          />
        ),
        cell: (cell: any) => (
          <input
            type="checkbox"
            className="tableCheckbox form-check-input"
            value={cell.getValue()}
            onChange={() => onSelectCheckbox()}
          />
        ),
        id: "checkBoxColumn",
        accessorKey: "id",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Title",
        accessorKey: "title",
        id: "title",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <div
              className="d-flex align-items-center"
              style={{ whiteSpace: "pre-wrap", width: "520px" }}
            >
              <div className="flex-shrink-0 me-3">
                <div className="avatar-sm">
                  <div className="avatar-title border bg-light text-danger rounded fs-24">
                    <FileIcon
                      fileName={
                        rowData?.reportFile &&
                        Array.isArray(rowData?.reportFile) &&
                        rowData?.reportFile[0]?.name
                      }
                    />
                  </div>
                </div>
              </div>
              <div className="flex-grow-1">
                <h5 className="fs-14 mb-1">
                  <Permission
                    alternative={<Link to={"#"} className="text-body">{cell.getValue()}</Link>}
                    action="findOne"
                    resource="assessment"
                  >
                    <Link
                      to={`/assessments/all-assessments/${rowData.id}`}
                      className="text-body"
                    >
                      {cell.getValue()}
                    </Link>
                  </Permission>
                </h5>
                <p className="text-muted mb-0">
                  Type:
                  <span className="fw-medium ms-1">
                    {rowData?.assessmentType?.name || "-"}
                  </span>
                </p>
              </div>
            </div>
          );
        },
      },
      {
        header: "Contractor",
        accessorKey: "contractor.contractorName",
        id: "contractor.contractorName",
        enableColumnFilter: false,
        cell: (cell: any) => <>{cell.row.original?.contractor?.contractorName || "-"}</>,
      },
      {
        header: "Agency",
        accessorKey: "agency.name",
        id: "agency.name",
        enableColumnFilter: false,
        cell: (cell: any) => <>{cell.row.original?.agency?.name || "-"}</>,
      },
      {
        header: "Overall Rating",
        accessorKey: "overallRiskRating.name",
        id: "overallScore",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          if (rowData?.overallRiskRating?.name) {
            return (
              <RiskAssessmentRating
                rating={rowData.overallRiskRating.name}
                solid={false}
                fs="fs-12"
              />
            );
          }

          if (rowData?.overallRating?.name) {
            return <CapacityAssessmentRating rating={rowData.overallRating.name} solid />;
          }

          return <>-</>;
        },
      },
      {
        header: "Report Date",
        accessorKey: "reportDate",
        id: "reportDate",
        enableColumnFilter: false,
        cell: (cell: any) => (
          <>{cell.getValue() ? moment(new Date(cell.getValue())).format("DD MMM Y") : "-"}</>
        ),
      },
      {
        header: "Action",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <UncontrolledDropdown>
              <DropdownToggle
                href="#"
                className="btn btn-soft-secondary btn-sm"
                tag="button"
              >
                <i className="ri-more-fill" />
              </DropdownToggle>
              <DropdownMenu className="dropdown-menu-end">
                <Permission action="findOne" resource="assessment">
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(`/assessments/all-assessments/${rowData.id}`);
                    }}
                  >
                    <i className="ri-eye-fill align-bottom me-2 text-muted"></i>
                    View
                  </DropdownItem>
                </Permission>
                <Permission action="update" resource="assessment">
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedRecord(rowData);
                      setIsUpdateModalOpen(true);
                    }}
                  >
                    <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
                    Edit
                  </DropdownItem>
                </Permission>
                <Permission action="delete" resource="assessment">
                  <DropdownItem divider />
                  <DropdownItem
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onClickDelete(rowData);
                    }}
                  >
                    <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>
                    Delete
                  </DropdownItem>
                </Permission>
              </DropdownMenu>
            </UncontrolledDropdown>
          );
        },
      },
    ],
    [checkedAll, navigate, onClickDelete]
  );

  document.title = "All Assessments | CIMS";

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="All Assessments"
          pageTitles={[
            { title: "Dashboard", link: "/dashboard" },
            { title: "Assessments", link: "/assessments/all-assessments" },
          ]}
        />

        <Row>
          <Col xl={12} lg={12}>
            <Card>
              <CardHeader>
                <Row className="align-items-center">
                  <Col md={8}>
                    <h5 className="card-title mb-1">All Assessments</h5>
                    <p className="text-muted mb-0">
                      This page shows all assessments, including risk,
                      capacity, and partner verification style records.
                    </p>
                  </Col>
                  <div className="col-md-auto ms-auto">
                    <div className="d-flex gap-2">
                      <Permissions checks={[{ resource: "assessment", action: "delete" }]}>
                        {selectedCheckBox.length > 0 && (
                          <UncontrolledButtonDropdown>
                            <DropdownToggle
                              color="secondary"
                              caret
                              className="material-shadow-none btn-soft-secondary"
                            >
                              Bulk Actions - {selectedCheckBox.length}
                            </DropdownToggle>
                            <DropdownMenu>
                              <DropdownItem
                                onClick={() => {
                                  setDeleteMultipleModal(true);
                                }}
                              >
                                <i className="ri-delete-bin-line align-middle me-2"></i>
                                Delete
                              </DropdownItem>
                            </DropdownMenu>
                          </UncontrolledButtonDropdown>
                        )}
                      </Permissions>

                      <Permission action="create" resource="assessment">
                        <Link
                          to="/#"
                          className="btn btn-primary"
                          onClick={(e: any) => {
                            e.preventDefault();
                            setIsAddModalOpen(true);
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>
                          Add Assessment
                        </Link>
                      </Permission>

                      <Permission action="import" resource="assessment">
                        <Import />
                      </Permission>
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col sm={4}>
                    <div className="search-box me-2 d-inline-block col-12">
                      <input
                        onChange={(e) => {
                          setSearchValue(e.target.value);
                          setPage(1);
                        }}
                        id="search-bar-0"
                        type="text"
                        className="form-control search"
                        placeholder="Search assessments"
                        value={searchValue || ""}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </Col>
                  <Col sm={3}>
                    <Select
                      name="assessmentTypeFilter"
                      id="assessmentTypeFilter"
                      value={filterValue?.assessmentType || null}
                      options={assessmentTypeOptions}
                      placeholder="Filter by assessment type"
                      onChange={(value: any) => {
                        setFilterValue({
                          ...filterValue,
                          assessmentType: value,
                        });
                        setPage(1);
                      }}
                      isClearable
                    />
                  </Col>
                  <Col sm={3}>
                    {/* @ts-ignore */}
                    <Flatpickr
                      className="form-control"
                      placeholder="Select Date Range"
                      options={{
                        mode: "range",
                        altFormat: "d M, Y",
                        dateFormat: "d M, Y",
                      }}
                      onChange={(dates) => {
                        if (dates[0] && dates[1]) {
                          setFilterValue({
                            ...filterValue,
                            dateRange: {
                              fromDate: dates[0],
                              toDate: dates[1],
                            },
                          });
                          setPage(1);
                        }
                      }}
                      value={[
                        filterValue?.dateRange?.fromDate || "",
                        filterValue?.dateRange?.toDate || "",
                      ]}
                    />
                  </Col>
                  <Col sm={2}>
                    <button
                      className="btn btn-light material-shadow-none w-100"
                      onClick={(e) => {
                        e.preventDefault();
                        setSearchValue("");
                        setFilterValue(defaultFilterValue);
                        setPage(1);
                        setSorted([{ id: "createdAt", desc: true }]);
                      }}
                    >
                      <i className="ri-restart-line me-1 align-bottom"></i>
                      Reset
                    </button>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card>
              <CardBody>
                <TableContainer
                  loading={loading}
                  columns={columns}
                  data={data || []}
                  pagination={pagination}
                  setPagination={setPagination}
                  sorted={sorted}
                  setSorted={setSorted}
                  onPageChange={onPageChange}
                  FilterSection={() => { }}
                  totalRows={meta?.pagination?.total || 0}
                  totalPages={meta?.pagination?.pageCount || 0}
                  searchValue={searchValue}
                  filterValue={filterValue}
                  columnVisibility={columnVisibility}
                  divClass="table-responsive table-card mb-4"
                  tableClass="align-middle table-nowrap mb-0 table-striped"
                  theadClass="table-nowrap"
                  thClass="pt-3"
                />

                <AddAssessment
                  toggle={() => setIsAddModalOpen((state) => !state)}
                  isModalOpen={isAddModalOpen}
                  fetchData={fetchData}
                />
                <UpdateAssessment
                  toggle={() => setIsUpdateModalOpen((state) => !state)}
                  isModalOpen={isUpdateModalOpen}
                  fetchUpdatedData={fetchUpdatedData}
                  selectedRecord={selectedRecord}
                />
                <DeleteModal
                  show={deleteModal}
                  onDeleteClick={() => handleDelete()}
                  onCloseClick={() => setDeleteModal(false)}
                  loading={deleting}
                />
                <DeleteModal
                  show={deleteMultipleModal}
                  onDeleteClick={() => handleDeleteMultiple()}
                  onCloseClick={() => setDeleteMultipleModal(false)}
                  loading={deleting}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default AllAssessments;
